import { ICellRendererParams } from 'ag-grid-community';
import {
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
  inject,
} from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';

import { AlertApiClientService } from '../../services/alert-api-client.service';
import { sanitizeEmoji } from '../../utils/sanitize-input.util';

@Component({
  selector: 'app-subject-renderer',
  templateUrl: './subject-renderer.component.html',
  styleUrls: ['./subject-renderer.component.scss'],
  standalone: false,
})
export class SubjectRendererComponent implements OnDestroy {
  private readonly alertApiClientService = inject(AlertApiClientService);

  @ViewChild('subjectInput') subjectInput!: ElementRef;
  @ViewChild('tooltip') errorTooltip!: MatTooltip;

  params!: ICellRendererParams;
  subject!: string;
  isEditing = false;
  isError = false;
  private hasBeenDestroyed = false;
  private focusTimeoutId?: ReturnType<typeof setTimeout>;
  private tooltipTimeoutId?: ReturnType<typeof setTimeout>;

  ngOnDestroy(): void {
    this.hasBeenDestroyed = true;
    if (this.focusTimeoutId) {
      clearTimeout(this.focusTimeoutId);
      this.focusTimeoutId = undefined;
    }
    if (this.tooltipTimeoutId) {
      clearTimeout(this.tooltipTimeoutId);
      this.tooltipTimeoutId = undefined;
    }
  }

  agInit(params: ICellRendererParams): void {
    this.params = params;
    this.subject = params.data.subject;
  }

  startEdit(): void {
    this.isEditing = true;
    this.focusTimeoutId = setTimeout(() => {
      if (!this.hasBeenDestroyed && this.subjectInput?.nativeElement) {
        this.subjectInput.nativeElement.focus();
      }
    });
  }

  saveEdit(event: any): void {
    event.preventDefault();
    if (event.key === 'Enter' || event.type === 'mousedown') {
      if (this.subject?.trim().length === 0) {
        return;
      }
      this.isEditing = false;
      this.updateSubject();
    } else if (event.key === 'Escape') {
      this.cancelEdit();
    }
  }

  cancelEdit(): void {
    this.hideErrorTooltip();
    this.isEditing = false;
    this.subject = this.params.data.subject;
  }

  private updateSubject(): void {
    if (this.params.data.subject !== this.subject?.trim()) {
      this.params.data.subject = this.subject;
      this.alertApiClientService
        .updateAlert(this.params.data)
        .subscribe((updated) => {
          this.params.node.setData(updated);
        });
    } else {
      this.cancelEdit();
    }
  }

  private showErrorTooltip(): void {
    this.isError = true;
    if (this.hasBeenDestroyed || !this.errorTooltip) {
      return;
    }
    this.errorTooltip.disabled = false;
    this.tooltipTimeoutId = setTimeout(() => {
      if (!this.hasBeenDestroyed && this.errorTooltip) {
        this.errorTooltip.show();
      }
    });
  }

  private hideErrorTooltip(): void {
    this.isError = false;
    if (this.tooltipTimeoutId) {
      clearTimeout(this.tooltipTimeoutId);
      this.tooltipTimeoutId = undefined;
    }
    if (this.hasBeenDestroyed || !this.errorTooltip) {
      return;
    }
    this.errorTooltip.hide();
    this.errorTooltip.disabled = true;
  }

  handleInput(event: Event): void {
    this.subject = sanitizeEmoji(event);

    if (this.subject.length >= 300) {
      this.showErrorTooltip();
    } else {
      this.hideErrorTooltip();
    }
  }
}
